import { QueryFunction, useQueries, useQuery, UseQueryOptions, UseQueryResult } from 'react-query'

import qs from 'query-string'

import { isoDateUTC } from '@percept/utils'

import { apiClients, makeClientQueryFn } from './lib'

import { Dictionary, ReportProviderV2 } from '@percept/types'


export type RateComparisonDatum = {
  current_rate: number
  prior_rate: number
  change: number
}

export type EffectivenessDatum = {
  rate_type: string
  competitor_group: RateComparisonDatum & {
    group_id: string
    group_name: string
  }
  org_activity: RateComparisonDatum
  relative_change: number
}

export type OrgUnitBenchmarkDatum = {
  org_unit_id: string
  org_unit_name: string
  outcomes: RateComparisonDatum
  effectiveness: EffectivenessDatum
}

export type ProviderSegment = {
  segment: string
}

export type ProviderSegmentBenchmarks = {
  provider_segment: ProviderSegment
  results: OrgUnitBenchmarkDatum[]
}

export type ProviderBenchmarks = {
  provider: ReportProviderV2
  current_month: string
  prior_month: string
  results: ProviderSegmentBenchmarks[]
}

export type ProviderBenchmarksHookProps = {
  provider: ReportProviderV2
  orgUnitIds: string[]
  baselineStart: Date | null
  baselineEnd: Date | null
  comparisonStart: Date | null
  comparisonEnd: Date | null
} & UseQueryOptions<ProviderBenchmarks>

const dateToString = (d: Date | null): string | null => (
  d && isoDateUTC(d)
)

const getDateParams = (
  dates: Pick<ProviderBenchmarksHookProps, 'comparisonStart' | 'comparisonEnd' | 'baselineStart' | 'baselineEnd'>
): Record<string, string | null> => {
  return {
    benchmark_start: dateToString(dates.baselineStart),
    benchmark_end: dateToString(dates.baselineEnd),
    comparison_start: dateToString(dates.comparisonStart),
    comparison_end: dateToString(dates.comparisonEnd),
  }
}

const getBenchmarkingProviderQueryFn = (provider: ReportProviderV2, params: Dictionary): QueryFunction<ProviderBenchmarks> => (
  makeClientQueryFn(
    apiClients.core,
    {
      url: `/benchmarking/${provider}`,
      params,
      paramsSerializer: qs.stringify,
    }
  )
)

export const useProviderBenchmarks = ({
  provider,
  orgUnitIds,
  baselineStart,
  baselineEnd,
  comparisonStart,
  comparisonEnd,
  enabled = true,
  ...queryOptions
}: ProviderBenchmarksHookProps): UseQueryResult<ProviderBenchmarks> => {
  const params = {
    org_unit_ids: orgUnitIds,
    ...getDateParams({ comparisonStart, comparisonEnd, baselineStart, baselineEnd }),
  }
  return useQuery<ProviderBenchmarks>({
    queryKey: ['benchmarking', provider, orgUnitIds && orgUnitIds.sort(), baselineStart, baselineEnd, comparisonStart, comparisonEnd],
    queryFn: getBenchmarkingProviderQueryFn(provider, params),
    enabled: !!(enabled && baselineStart && baselineEnd && comparisonStart && comparisonEnd && orgUnitIds && orgUnitIds.length && provider),
    retry: false,
    cacheTime: Infinity,
    ...queryOptions,
  })
}


export const useAllProviderBenchmarks = ({
  orgUnitId,
  providers = ['ADFORM', 'DV360', 'FACEBOOK', 'GOOGLE_ADS', 'TIKTOK'],
  baselineStart,
  baselineEnd,
  comparisonStart,
  comparisonEnd,
  enabled = true,
  ...queryOptions
}: Omit<
  ProviderBenchmarksHookProps,
  'orgUnitIds' | 'provider'
  > & {
    orgUnitId: string | null
    providers?: ReportProviderV2[]
  }
): UseQueryResult<ProviderBenchmarks>[] => {
  const params = {
    org_unit_ids: [orgUnitId],
    ...getDateParams({ comparisonStart, comparisonEnd, baselineStart, baselineEnd }),
  }
  enabled = !!(enabled && baselineStart && baselineEnd && comparisonStart && comparisonEnd && orgUnitId)
  return useQueries(
    providers.map( provider => ({
      queryKey: ['benchmarking', orgUnitId, provider, baselineStart, baselineEnd, comparisonStart, comparisonEnd],
      queryFn: getBenchmarkingProviderQueryFn(provider, params),
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      enabled: enabled && !!provider ,
      ...queryOptions,
    }))
  )
}


type MonthPeriod = Record<'start_date' | 'end_date', string>

type VariantMonthPeriod = MonthPeriod & { variant: string }

export type ProviderBenchmarkAvailability = {
  provider: ReportProviderV2
  available_periods: VariantMonthPeriod[]
}

export const useProviderBenchmarkAvailability = (
  queryOptions?: UseQueryOptions<ProviderBenchmarkAvailability[]>
): UseQueryResult<ProviderBenchmarkAvailability[]> => {
  return useQuery<ProviderBenchmarkAvailability[]>({
    queryKey: ['benchmarking', 'availability'],
    queryFn: makeClientQueryFn(
      apiClients.core,
      {
        url: '/benchmarking/availability',
      }
    ),
    retry: false,
    cacheTime: Infinity,
    ...queryOptions,
  })
}
